import * as React from 'react';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { SurveyPage } from '@quality24/inpatient-typings';
import {
  Heading,
  Icon,
  SelectionContainer,
  Text,
  TextArea,
} from '@quality24/design-system';

import { SurveyHook } from '@/inpatient-patient-pwa/hooks/useSurvey';
import ImprovementsSelector from '@/inpatient-patient-pwa/molecules/ImprovementsSelector';
import useImprovements from '../useImprovements';

export interface Props {
  page: Pick<
    SurveyPage,
    'id' | 'category' | 'type' | 'titleI18N' | 'descriptionI18N' | 'data'
  >;
  answer: SurveyHook['answer'];
}

/**
 * Renders a `YES_NO` survey question.
 *
 * @deprecated `YES_NO` questions are no longer authored, so this page is kept
 * only for surveys already released. Slated for removal along with the `YES_NO`
 * branch in `DailySurvey` — mind that it answers with `score: 0` for 👎, which
 * the truthiness checks in `useSurvey` read as unanswered.
 */
const YesOrNoQuestionPage: React.FunctionComponent<Props> = ({
  page,
  answer,
}) => {
  const { t } = useTranslation();
  const description = page.descriptionI18N;
  const improvements = useImprovements(page.data);

  const show =
    answer.values.value.score !== undefined && answer.values.value.score === 0;

  return (
    <>
      <Heading as="h1" weight="semiBold" size="lg1">
        {page.titleI18N}
      </Heading>
      <Text className="my-4" style={{ whiteSpace: 'pre-wrap' }}>
        {description}
      </Text>

      <SelectionContainer
        className="d-flex flex-justify-center"
        allowMultiple={false}
        value={answer.values.value.score}
        onChange={(v) => {
          answer.setFieldValue('value.score', v);
          // Selecting 👍 (or deselecting) hides the improvements below, so drop
          // whatever was picked instead of submitting it with the new score
          if (v !== 0) {
            answer.setFieldValue('value.improvements', undefined);
          }
        }}
      >
        <SelectionContainer.Option
          value={1}
          className="py-4 px-5 rounded-md bg-nps10 flex-1"
          style={{ flex: 1 }}
        >
          <Icon icon={faThumbsUp} color="white" size="lg" />
        </SelectionContainer.Option>
        <SelectionContainer.Option
          value={0}
          className="py-4 px-5 rounded-md bg-nps0 flex-1"
          style={{ flex: 1 }}
        >
          <Icon icon={faThumbsDown} color="white" size="lg" />
        </SelectionContainer.Option>
      </SelectionContainer>

      {show && improvements.titles.length > 0 && (
        <div className="my-4">
          <ImprovementsSelector
            titles={improvements.titles}
            value={improvements.toDisplayed(
              answer.values.value.improvements || [],
            )}
            onChange={(v: string[]) =>
              answer.setFieldValue(
                'value.improvements',
                improvements.toStored(v),
              )
            }
          />
        </div>
      )}

      <TextArea
        id="formFeedback"
        className="my-4"
        label={t('survey.comment.label')}
        name="text"
        value={answer.values.value.text}
        errorText={answer.errors?.value?.text}
        onChange={(e) => answer.setFieldValue('value.text', e.target.value)}
      />
    </>
  );
};

export default YesOrNoQuestionPage;
